import { useQuery } from '@tanstack/react-query';
import {
  getDashboardData,
  getDashboardHealth,
  getDashboardTimeSeries,
  getDashboardEntityMetrics,
} from '@/api/dashboard-api';
import { useCompanyContextStore } from '@/store/company-context-store';

/**
 * Effective companyId — super-admin override takes precedence over the user's
 * home company. Embedded in every dashboard queryKey so React Query treats
 * each company's data as an independent cache (no stale-cross-tenant flash
 * when switching companies via the context switcher).
 */
function useScopedCompanyId(): string | null {
  return useCompanyContextStore((s) => s.activeCompanyId ?? s.companyId);
}

export function useDashboardData() {
  const companyId = useScopedCompanyId();
  return useQuery({
    queryKey: ['dashboard', companyId],
    queryFn: getDashboardData,
    enabled: !!companyId,
    refetchInterval: 30_000, // Refresh every 30 seconds
    staleTime: 15_000, // Consider data stale after 15 seconds
  });
}

export function useDashboardHealth() {
  const companyId = useScopedCompanyId();
  return useQuery({
    queryKey: ['dashboard', 'health', companyId],
    queryFn: getDashboardHealth,
    enabled: !!companyId,
    refetchInterval: 15_000, // Refresh health more frequently
    staleTime: 10_000,
  });
}

export function useDashboardTimeSeries(range: '24h' | '7d' | '30d' = '24h') {
  const companyId = useScopedCompanyId();
  return useQuery({
    queryKey: ['dashboard', 'timeseries', companyId, range],
    queryFn: () => getDashboardTimeSeries(range),
    enabled: !!companyId,
    refetchInterval: 60_000, // Refresh every 60 seconds
    staleTime: 30_000,
  });
}

export function useDashboardEntityMetrics() {
  const companyId = useScopedCompanyId();
  return useQuery({
    queryKey: ['dashboard', 'entity-metrics', companyId],
    queryFn: getDashboardEntityMetrics,
    enabled: !!companyId,
    refetchInterval: 60_000, // Refresh every 60 seconds
    staleTime: 30_000,
  });
}
